import { Parser, type Expression } from "expr-eval";

const parser = new Parser();
const COMPILED = new Map<string, Expression>();

function compile(formula: string): Expression {
  let expr = COMPILED.get(formula);
  if (!expr) {
    expr = parser.parse(formula);
    COMPILED.set(formula, expr);
  }
  return expr;
}

type round = {
  rounding?: string;
  roundingDigits?: number;
};

export function calculateMaterialCodePrice(
  basePrice: number,
  formulaParameters: Record<string, number | boolean | string>,
  othersCostDetail: Array<{ id: number; cost: number }>,
  formula: string,
  opts: round = {}
): number {
  if (!formula || formula.trim() === "") {
    return 0;
  }
  const { rounding = "ROUND", roundingDigits = 0 } = opts;

  const expr = compile(formula);
  const requiredVars = expr.variables();

  const ctx: Record<string, number> = { base_price: Number(basePrice) };

  for (const name of requiredVars) {
    if (name === "base_price") continue;
    const val = formulaParameters[name];

    if (val === undefined || val === null || val === "") {
      ctx[name] = 0;
      continue;
    }

    if (typeof val === "boolean") {
      ctx[name] = val ? 1 : 0;
    } else if (typeof val === "number") {
      ctx[name] = Number.isNaN(val) ? 0 : val;
    } else {
      const num = Number(val);
      ctx[name] = Number.isNaN(num) ? 0 : num;
    }
  }

  ctx["others_cost"] = othersCostDetail.reduce((sum, item) => sum + item.cost, 0);
  
  let result = expr.evaluate(ctx);
  switch (rounding) {
    case "ROUND UP":
      if (roundingDigits) {
        const p = 10 ** roundingDigits;
        result = Math.ceil(result * p) / p;
      } else {
        result = Math.ceil(result);
      }
      break;
    case "ROUND DOWN":
      if (roundingDigits) {
        const p = 10 ** roundingDigits;
        result = Math.floor(result * p) / p;
      } else {
        result = Math.floor(result);
      }
      break;
    case "ROUND":
      if (roundingDigits) {
        const p = 10 ** roundingDigits;
        result = Math.round(result * p) / p;
      } else {
        result = Math.round(result);
      }
      break;
    default:
      break;
  }

  return result;
}

export const createMatCode = (
  materialSupplierCode: string,
  firstTierSupplierCode: string,
  secondTierSupplierCode: string,
  returnScrapSupplierName: string,
  materialSpecName: string,
  dimensionName: string,
  dimensionParameters: Record<string, number>
): string => {
  const getVal = (key: string): number => {
    if (!dimensionParameters) return 0;

    const found = Object.entries(dimensionParameters).find(
      ([k]) => k.toLowerCase() === key.toLowerCase()
    );

    return found ? found[1] : 0;
  };

  let dimSuffix = "";

  switch (dimensionName.toLowerCase()) {
    case "bar": {
      const d = getVal("diameter");
      dimSuffix = `_${d}`;
      break;
    }

    case "rectangular pipe": {
      const x = getVal("rectangular_x");
      const y = getVal("rectangular_y");
      const t = getVal("thickness");
      const l = getVal("length_mm");
      dimSuffix = `_${x}x${y}_${t}t_${l}`;
      break;
    }

    case "round pipe": {
      const r = getVal("round_od");
      const t = getVal("thickness");
      const l = getVal("length_mm");
      dimSuffix = `_${r}Ø_${t}t_${l}`;
      break;
    }

    case "shaft": {
      const l = getVal("length_mm");
      const s = getVal("shaft_od");
      dimSuffix = `_${s}Ø_${l}`;
      break;
    }

    case "sheet": {
      const w = getVal("width");
      const l = getVal("length_mm");
      const t = getVal("thickness");
      dimSuffix = `_${t}_${w}_${l}`;
      break;
    }

    default:
      dimSuffix = "";
  }

  return `${materialSupplierCode}_${secondTierSupplierCode || "None"}_${returnScrapSupplierName || "None"}_${firstTierSupplierCode}_${materialSpecName}${dimSuffix}`;
};
